import React from 'react';
// eslint-disable-next-line import/no-namespace
import * as Dialog from '@radix-ui/react-dialog';
import { RemoveScroll } from 'react-remove-scroll';

import { cn } from 'helpers/classnames';
import IconButton from 'components/common/IconButton';
import CloseIcon from 'components/common/IconsV2/CloseIcon';
import useMartyContext from 'hooks/useMartyContext';

const DrawerRoot = Dialog.Root;

const DrawerTrigger = Dialog.Trigger;

const DrawerPortal = Dialog.Portal;

const DrawerClose = Dialog.Close;

const DrawerOverlay = ({ className, shouldAnimate, ...props }: React.ComponentProps<'div'> & { shouldAnimate?: boolean }) => (
  // Plain div instead of Dialog.Overlay saves ~130ms INP on mid-tier devices.
  // RemoveScroll preserves scroll locking and pointer-events that Dialog.Overlay normally handles.
  <RemoveScroll>
    <div
      data-state="open"
      aria-hidden="true"
      className={cn(
        'pointer-events-auto fixed inset-0 z-[998] bg-overlay',
        shouldAnimate &&
          'ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  </RemoveScroll>
);

const DrawerContent = ({
  className,
  children,
  shouldAnimate,
  ...props
}: React.ComponentProps<typeof Dialog.Content> & { shouldAnimate?: boolean }) => (
  <DrawerPortal>
    <DrawerOverlay shouldAnimate={shouldAnimate} />
    <Dialog.Content
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[999] flex flex-col overflow-hidden rounded-t-2xl bg-gray-0 focus:z-[999]',
        shouldAnimate && [
          'ease-out data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-[100%]',
          'ease-out data-[state=closed]:duration-300 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-[100%]'
        ],
        className
      )}
      {...props}
    >
      {children}
    </Dialog.Content>
  </DrawerPortal>
);

const DrawerTitle = ({ className, ...props }: React.ComponentProps<typeof Dialog.Title>) => (
  <Dialog.Title className={cn('body-l-medium', className)} {...props} />
);

const DrawerHeader = ({ className, children, previousSlot, ...props }: React.ComponentProps<'div'> & { previousSlot?: React.ReactNode }) => {
  const { testId } = useMartyContext();
  return (
    <div className={cn('grid grid-cols-[1fr_auto_1fr] items-center border-b border-b-gray-300 bg-gray-0 px-5 py-4', className)} {...props}>
      <DrawerPrevious className="flex aspect-square size-8 shrink-0 justify-self-start">{previousSlot}</DrawerPrevious>

      <DrawerTitle className="flex w-full items-center justify-center justify-self-center">{children}</DrawerTitle>

      <DrawerClose asChild>
        <IconButton
          label={'Close Drawer'}
          size="base"
          variant="icon"
          className="flex aspect-square size-8 min-w-0 shrink-0 justify-self-end"
          data-test-id={testId('closeDrawer')}
        >
          <CloseIcon size={24} />
        </IconButton>
      </DrawerClose>
    </div>
  );
};

const DrawerPrevious = ({ className, ...props }: React.ComponentProps<'div'>) => <div className={cn('flex shrink-0', className)} {...props} />;

const DrawerFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('border-t border-t-gray-300 bg-gray-0 px-5 py-4', className)} {...props} />
);

export const Drawer = {
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Portal: DrawerPortal,
  Overlay: DrawerOverlay,
  Content: DrawerContent,
  Close: DrawerClose,
  Title: DrawerTitle,
  Header: DrawerHeader,
  Previous: DrawerPrevious,
  Footer: DrawerFooter
};
